import { LitElement, html, css } from 'lit';

export class ProgressTracker extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .progress-container {
      background: white;
      border-radius: 20px;
      padding: 24px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }

    .title {
      font-size: 1.25rem;
      font-weight: 700;
      color: #333;
    }

    .reset-btn {
      padding: 8px 16px;
      background: rgba(244, 67, 54, 0.1);
      color: #f44336;
      border: none;
      border-radius: 8px;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s ease;
    }

    .reset-btn:hover {
      background: rgba(244, 67, 54, 0.2);
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-bottom: 24px;
    }

    .stat-card {
      text-align: center;
      padding: 16px;
      background: rgba(102, 126, 234, 0.05);
      border-radius: 12px;
    }

    .stat-value {
      font-size: 1.75rem;
      font-weight: 700;
      color: #667eea;
      margin-bottom: 4px;
    }

    .stat-label {
      font-size: 0.75rem;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .section-title {
      font-size: 1rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 12px;
    }

    .word-list {
      max-height: 300px;
      overflow-y: auto;
    }

    .word-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border-radius: 10px;
      margin-bottom: 8px;
      background: #f8f9fa;
      transition: background 0.2s ease;
    }

    .word-item:hover {
      background: #e9ecef;
    }

    .word-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .word-name {
      font-weight: 600;
      color: #333;
    }

    .word-meaning {
      font-size: 0.875rem;
      color: #666;
    }

    .word-status {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .status-badge {
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 500;
      text-transform: uppercase;
    }

    .status-badge.mastered {
      background: rgba(76, 175, 80, 0.15);
      color: #4caf50;
    }

    .status-badge.learning {
      background: rgba(255, 152, 0, 0.15);
      color: #ff9800;
    }

    .status-badge.new {
      background: rgba(158, 158, 158, 0.15);
      color: #9e9e9e;
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: #666;
    }

    .empty-state svg {
      width: 64px;
      height: 64px;
      margin-bottom: 16px;
      opacity: 0.5;
    }

    .empty-state-text {
      font-size: 1rem;
      margin-bottom: 8px;
    }

    .empty-state-hint {
      font-size: 0.875rem;
      opacity: 0.7;
    }

    .difficulty-breakdown {
      display: flex;
      gap: 12px;
      margin-bottom: 20px;
    }

    .difficulty-item {
      flex: 1;
      text-align: center;
      padding: 12px;
      border-radius: 10px;
    }

    .difficulty-item.easy {
      background: rgba(76, 175, 80, 0.1);
    }

    .difficulty-item.medium {
      background: rgba(255, 152, 0, 0.1);
    }

    .difficulty-item.hard {
      background: rgba(244, 67, 54, 0.1);
    }

    .difficulty-count {
      font-size: 1.25rem;
      font-weight: 700;
    }

    .difficulty-item.easy .difficulty-count {
      color: #4caf50;
    }

    .difficulty-item.medium .difficulty-count {
      color: #ff9800;
    }

    .difficulty-item.hard .difficulty-count {
      color: #f44336;
    }

    .difficulty-label {
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #666;
    }

    @media (max-width: 480px) {
      .progress-container {
        padding: 20px;
      }

      .stats-grid {
        gap: 12px;
      }

      .stat-card {
        padding: 12px;
      }

      .stat-value {
        font-size: 1.5rem;
      }

      .difficulty-breakdown {
        gap: 8px;
      }

      .difficulty-item {
        padding: 10px;
      }
    }
  `;

  static properties = {
    progress: { type: Object },
    words: { type: Array }
  };

  constructor() {
    super();
    this.progress = {
      mastered: [],
      learning: [],
      attempts: 0,
      correct: 0
    };
    this.words = [];
  }

  _getWordStatus(wordId) {
    if (this.progress.mastered.includes(wordId)) return 'mastered';
    if (this.progress.learning.includes(wordId)) return 'learning';
    return 'new';
  }

  _getWordList() {
    return this.words.map(word => ({
      ...word,
      status: this._getWordStatus(word.id)
    })).sort((a, b) => {
      const statusOrder = { mastered: 0, learning: 1, new: 2 };
      return statusOrder[a.status] - statusOrder[b.status];
    });
  }

  _getDifficultyCounts() {
    const counts = { easy: 0, medium: 0, hard: 0 };
    this.words.forEach(word => {
      if (this.progress.mastered.includes(word.id)) {
        counts[word.difficulty]++;
      }
    });
    return counts;
  }

  _resetProgress() {
    this.dispatchEvent(new CustomEvent('reset-progress', {
      bubbles: true,
      composed: true
    }));
  }

  render() {
    const wordList = this._getWordList();
    const difficultyCounts = this._getDifficultyCounts();
    const accuracy = this.progress.attempts > 0 
      ? Math.round((this.progress.correct / this.progress.attempts) * 100) 
      : 0;

    return html`
      <div class="progress-container">
        <div class="header">
          <div class="title">Jouw Voortgang</div>
          <button class="reset-btn" @click="${this._resetProgress}">Reset</button>
        </div>

        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">${this.progress.mastered.length}</div>
            <div class="stat-label">Beheerst</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${this.progress.learning.length}</div>
            <div class="stat-label">Leren</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${accuracy}%</div>
            <div class="stat-label">Nauwkeurigheid</div>
          </div>
        </div>

        <div class="section-title">Op Moeilijkheid</div>
        <div class="difficulty-breakdown">
          <div class="difficulty-item easy">
            <div class="difficulty-count">${difficultyCounts.easy}</div>
            <div class="difficulty-label">Makkelijk</div>
          </div>
          <div class="difficulty-item medium">
            <div class="difficulty-count">${difficultyCounts.medium}</div>
            <div class="difficulty-label">Gemiddeld</div>
          </div>
          <div class="difficulty-item hard">
            <div class="difficulty-count">${difficultyCounts.hard}</div>
            <div class="difficulty-label">Moeilijk</div>
          </div>
        </div>

        <div class="section-title">Woordstatus</div>
        ${wordList.length > 0 ? html`
          <div class="word-list">
            ${wordList.map(word => html`
              <div class="word-item">
                <div class="word-info">
                  <span class="word-name">${word.word}</span>
                  <span class="word-meaning">${word.meaning}</span>
                </div>
                <div class="word-status">
                  <span class="status-badge ${word.status}">${word.status === 'mastered' ? 'beheerst' : word.status === 'learning' ? 'leren' : 'nieuw'}</span>
                </div>
              </div>
            `)}
          </div>
        ` : html`
          <div class="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
            </svg>
            <div class="empty-state-text">Nog geen woorden</div>
            <div class="empty-state-hint">Begin met oefenen om je voortgang bij te houden!</div>
          </div>
        `}
      </div>
    `;
  }
}

customElements.define('progress-tracker', ProgressTracker);
